import { Inject, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { AutomationReplacementChipsDataService, PagedResponse } from '../common/components';
import { map, Observable, of, firstValueFrom, from, switchMap, takeUntil, Subject, BehaviorSubject } from 'rxjs';
import { YeswareCampaignsApiService } from '@vendasta/vendasta-yesware-bridge';
import { ListUsersCampaignsResponse } from '@vendasta/vendasta-yesware-bridge/lib/_internal/objects';
import { AUTOMATION_CONTEXT_INJECTION_TOKEN, AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { Context } from '@vendasta/automata';
import { shareReplay } from 'rxjs/operators';

export interface YeswareCampaign {
  campaign_id: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class YeswareCampaignSelectorService
  implements AutomationReplacementChipsDataService<YeswareCampaign>, OnDestroy
{
  supportsSearching = false;

  private readonly campaignsApiService = inject(YeswareCampaignsApiService);
  private campaignsSignal = signal<YeswareCampaign[]>([]);
  private loadingSignal = signal<boolean>(false);
  private currentNamespace: string | null = null;
  private destroy$ = new Subject<void>();

  // Add a campaigns loading observable that can be shared across subscribers
  private campaignsLoading$ = new BehaviorSubject<boolean>(false);
  private campaignsLoadedObservable: Observable<void> | null = null;

  constructor(
    @Inject(AUTOMATION_CONTEXT_INJECTION_TOKEN) private readonly automationContext: Context,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {
    // Clear the cache when namespace changes
    this.namespace$.pipe(takeUntil(this.destroy$)).subscribe((namespace) => {
      if (this.currentNamespace !== namespace) {
        this.clearCache();
        this.currentNamespace = namespace;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.campaignsLoading$.complete();
  }

  convertDataToDisplay(campaign: YeswareCampaign): Observable<string> {
    return of(campaign.name || '');
  }

  convertDataToFormValue(campaign: YeswareCampaign): any {
    return campaign.campaign_id;
  }

  private getNamespaceObject(namespaceValue: string): { partnerId?: string; accountGroupId?: string } {
    if (this.automationContext === Context.AUTOMATION_CONTEXT_PARTNER) {
      return { partnerId: namespaceValue };
    } else if (this.automationContext === Context.AUTOMATION_CONTEXT_SMB) {
      return { accountGroupId: namespaceValue };
    }
    // Default to partner context if unset
    return { partnerId: namespaceValue };
  }

  doLookup(searchTerm: string, cursor: string, pageSize: number, _: any): Observable<PagedResponse<YeswareCampaign>> {
    return this.ensureCampaignsLoaded().pipe(
      map(() => {
        const allCampaigns = this.campaignsSignal();

        // Filter campaigns by name if searchTerm is provided
        let filteredCampaigns = allCampaigns;
        if (searchTerm && searchTerm.trim() !== '') {
          const normalizedSearchTerm = searchTerm.toLowerCase().trim();
          filteredCampaigns = allCampaigns.filter((campaign) =>
            campaign.name.toLowerCase().includes(normalizedSearchTerm),
          );
        }

        // Calculate pagination
        const startIndex = cursor ? parseInt(cursor, 10) : 0;
        const endIndex = Math.min(startIndex + pageSize, filteredCampaigns.length);
        const results = filteredCampaigns.slice(startIndex, endIndex);

        return {
          results: results,
          nextCursor: endIndex < filteredCampaigns.length ? String(endIndex) : null,
          hasMore: endIndex < filteredCampaigns.length,
        } as PagedResponse<YeswareCampaign>;
      }),
    );
  }

  getMulti(formValues: any[], _?: any): Observable<YeswareCampaign[]> {
    // If there are no IDs to look up, return empty array
    if (!formValues || formValues.length === 0) {
      return of([]);
    }

    // Convert form values to campaign IDs
    const campaignIds = new Set(formValues.map((id) => String(id)));

    // Ensure campaigns are loaded, then filter by the requested IDs
    return this.ensureCampaignsLoaded().pipe(
      map(() => {
        const allCampaigns = this.campaignsSignal();
        return allCampaigns.filter((campaign) => campaignIds.has(campaign.campaign_id));
      }),
    );
  }

  /**
   * Ensures campaigns are loaded, loading them if needed.
   * Returns an Observable that emits when the campaigns are ready.
   */
  private ensureCampaignsLoaded(): Observable<void> {
    // If we already have campaigns loaded, just return
    if (this.campaignsSignal().length > 0) {
      return of(undefined);
    }

    // If we're already loading, return the existing observable
    if (this.campaignsLoadedObservable !== null && this.campaignsLoading$.getValue()) {
      return this.campaignsLoadedObservable;
    }

    // Create a new loading observable
    this.campaignsLoadedObservable = this.namespace$.pipe(
      switchMap((namespace) => {
        // Only fetch if we're not already loading
        if (this.campaignsLoading$.getValue()) {
          return of(undefined);
        }

        this.campaignsLoading$.next(true);
        this.loadingSignal.set(true);

        return from(
          this.fetchAllCampaigns(namespace).then((campaigns) => {
            this.campaignsSignal.set(campaigns);
            this.loadingSignal.set(false);
            this.campaignsLoading$.next(false);
          }),
        );
      }),
      // Critical: share the observable between subscribers
      shareReplay(1),
    );

    return this.campaignsLoadedObservable;
  }

  private async fetchAllCampaigns(namespace: string): Promise<YeswareCampaign[]> {
    const collectedCampaigns: YeswareCampaign[] = [];
    let cursor: string | undefined = '';
    let hasMore = true;

    while (hasMore) {
      // Get a page of campaigns
      const response: ListUsersCampaignsResponse = await firstValueFrom(
        this.campaignsApiService.listUsersCampaigns({
          pagingOptions: {
            cursor: cursor || '',
            pageSize: 100, // Use larger page size for efficiency
          },
          namespace: this.getNamespaceObject(namespace),
        }),
      );

      // Map to our campaign interface
      const campaigns = response.campaigns.map((campaign) => ({
        campaign_id: campaign.campaignId.toString(),
        name: campaign.name,
      }));

      // Add campaigns to our collection
      collectedCampaigns.push(...campaigns);

      // Update loop control variables
      cursor = response?.pagingMetadata?.nextCursor;
      hasMore = !!cursor && response?.pagingMetadata?.hasMore;
    }

    return collectedCampaigns;
  }

  private clearCache(): void {
    this.campaignsSignal.set([]);
    this.loadingSignal.set(false);
    this.campaignsLoading$.next(false);
    this.campaignsLoadedObservable = null;
  }
}
